import React, { useLayoutEffect, useRef, useState , useEffect} from 'react';
import { createRoot } from 'react-dom/client';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { SEARCHABLE_COLUMNS, CONTENT_DISPLAY_OPTIONS } from './table-select-filter-config';
import { Preferences } from '../commons/table-config';
import { parseAiRecommendationPercentageScore } from '../../common/utility';
import { CompletedStatusComponent } from './table-select-filter-components';  
import {
  Button,
  ButtonDropdown,
  Input,
  FormField,
  Pagination,
  SpaceBetween,
  Select,
  Table,
  TextFilter,
  Box, 
  Header,
  Link,
  StatusIndicator
} from '@cloudscape-design/components';
import { Navigation, Breadcrumbs, ToolsContent } from './table-select-filter-components';
import '../../styles/table-select.scss';
import { getTextFilterCounterText, getHeaderCounterText, renderAriaLive } from '../../i18n-strings';
import { CustomAppLayout, Notifications, TableEmptyState, TableNoMatchState } from '../commons/common-components';
import { FullPageHeader } from '../commons';
import { useColumnWidths } from '../commons/use-column-widths';
import { useLocalStorage } from '../commons/use-local-storage';
import { useParams } from 'react-router-dom';
import { submissionsApi } from '../../api/Submission';
import { useBreadcrumbs } from '../../components/breadcrumbs/index';

// Generate unique random ID
const generateRandomId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const COLUMN_DEFINITIONS = [
  {
    id: 'title',
    header: 'Title',
    cell: item => item.indicator?.name ? <Link href={`/indicator-submission/${item.id}`}>{item.indicator.index}: {item.indicator.name}</Link> : item.title,
    isRowHeader: true,
    width: 300
  },
  {
    id: 'is_complete',
    header: 'Status',
    cell: item => item.indicator ? <CompletedStatusComponent status={item.is_complete} /> : null,
  },
  {
    id: 'latest_score',
    header: 'AI Score',
    cell: item => (item.latest_score !== null && item.latest_score !== undefined && 
      !isNaN(Number(item.latest_score)) && Number(item.latest_score) >= 0 && 
      !(item && Array.isArray(item.indicator_submissions))) ? 
      `${parseAiRecommendationPercentageScore(item.latest_score)}%` : '',
  },
];



export default function ApplicationDetails({applicationID}) {

  const { setActiveBreadcrumb } = useBreadcrumbs();
  useEffect(() => {
    setActiveBreadcrumb(
      { text: 'Application summary', href: `/application-details/${applicationID}`, index: 3 },
      3
    );
  }, [applicationID, setActiveBreadcrumb]);
  
  const [items, setDATA] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const data = await submissionsApi.getIndicators(applicationID);
        
       
        // Add unique random IDs to avoid ID conflicts between frameworks
        const dataWithRandomIds = data.map(standard => ({
          ...standard,
          randomId: generateRandomId(),
          indicator_submissions: (standard.indicator_submissions || []).map(submission => ({
            ...submission,
            randomId: generateRandomId()
          }))
        }));
                
        setDATA(dataWithRandomIds);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (applicationID) {
      fetchData();
    }
  }, [applicationID]);

  const handleExpandableItemToggle = ({ detail }) => {
    
    setExpandedItems(prev => {
      const currentIds = new Set((prev ?? []).map(item => item.randomId));
      
      if (detail.expanded) {
        currentIds.add(detail.item.randomId);
      } else {
        currentIds.delete(detail.item.randomId);
      }
      
      const newExpandedItems = Array.from(currentIds).map(randomId => ({ randomId }));
      
      return newExpandedItems;
    });
  };

  if (isLoading) {
    return (
      <Box textAlign="center" padding="xl">
        <StatusIndicator type="loading">Loading application data...</StatusIndicator>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" padding="xl">
        <StatusIndicator type="error">
          Error loading data: {error.message}
        </StatusIndicator>
      </Box>
    );
  }

  return (
    <Table
      trackBy="randomId"
            renderAriaLive={({
        firstIndex,
        lastIndex,
        totalItemsCount
      }) =>
        `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
      }

      renderLoaderLoading={() => (
        <StatusIndicator type="loading">
          Loading items
        </StatusIndicator>
      )}
      
      renderLoaderError={() => (
        <StatusIndicator type="error">
          Loading error
        </StatusIndicator>
      )}
      
      renderLoaderEmpty={() => (
        <Box>No resources found</Box>
      )}

      expandableRows={{
        getItemChildren: item => {

          if (!item) {
            return [];
          }
          
          if (!item.indicator_submissions) {
            return [];
          }
          
          if (!Array.isArray(item.indicator_submissions)) {
            return [];
          }
          
          return item.indicator_submissions;
        },
        
        isItemExpandable: item => {
          const expandable = Boolean(
            item && 
            item.indicator_submissions && 
            Array.isArray(item.indicator_submissions) && 
            item.indicator_submissions.length > 0
          );
          
          return expandable;
        },
        
        expandedItems: expandedItems,
        onExpandableItemToggle: handleExpandableItemToggle
      }}
      columnDefinitions={COLUMN_DEFINITIONS}
      enableKeyboardNavigation
      
      getLoadingStatus={item =>
        !item
          ? "pending"
          : item.name === "Item 5"
          ? "loading"
          : item.name === "Item 6"
          ? "error"
          : "finished"
      }
      
      items={items}
      loadingText="Loading resources"
      header={
        <Header
          counter={items.length ? `(${items.length})` : ''}
          description="Standards and their indicators for this application"
        >
          Application Details
        </Header>
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>No indicators found</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            No indicators are available for this application.
          </Box>
        </Box>
      }
      
    />
  );
}